import React from "react";
import { observer } from "mobx-react";
import { formatMetric } from "../visuals/formatMetric";
import { resolveThreshold } from "../visuals/visualConfig";

/**
 * A filled track against a target or limit — the correct form for a ratio
 * bounded by a known maximum, and the replacement for a two-slice pie.
 *
 * The threshold is data, not decoration: it comes from the block (tool-supplied)
 * or, failing that, from visualConfig. If neither has one the meter refuses to
 * render, because a bar with an invented denominator is worse than no bar.
 *
 * `inverse` flips the judgement, not the drawing: bounce rate under its
 * threshold is good, deliverability under its threshold is bad.
 */
const MeterBlock = observer(({ block }) => {
  const threshold = resolveThreshold(block.threshold, block.kind);
  if (block.value == null || !threshold) return null;

  const value = Number(block.value);
  const limit = Number(threshold.value);
  if (!Number.isFinite(value) || !Number.isFinite(limit) || limit <= 0) return null;

  const unit = block.unit || threshold.unit || "";
  // Scale the track to whichever is larger so an overshoot is VISIBLE. Clamping
  // the fill at 100% painted the whole bar red and lost the one thing the user
  // wants to know: how far over the line they are.
  const trackMax = Math.max(value, limit) * 1.08;
  const ratio = trackMax > 0 ? value / trackMax : 0;
  const markerAt = trackMax > 0 ? (limit / trackMax) * 100 : 100;
  const inverse = threshold.inverse === true;
  // Meeting the bar is good normally; staying under it is good when inverse.
  const healthy = inverse ? value <= limit : value >= limit;
  const tone = healthy ? "success" : "danger";

  return (
    <div className="cpVisual cpVisual--meter">
      {block.title && <div className="cpVisual__title">{block.title}</div>}

      <div className="cpMeter__head">
        <span className="cpMeter__value">{formatMetric(value, { unit })}</span>
        {/* "of X" is right for a target you are working towards. On an
            inverse metric X is a ceiling, and "0% of 3%" read as "0 out of 3"
            -- as if a 0% bounce rate were no progress rather than the best
            possible result. */}
        <span className="cpMeter__limit">
          {inverse
            ? `limit ${formatMetric(limit, { unit })}`
            : `of ${formatMetric(limit, { unit })}`}
        </span>
      </div>

      <div
        className="cpMeter__track"
        role="meter"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-label={block.title || "meter"}
      >
        <div
          className={`cpMeter__fill cpMeter__fill--${tone}`}
          style={{ width: `${ratio * 100}%` }}
        />
        {/* Marker sits at the threshold so "how close am I" is readable
            without doing arithmetic. */}
        <div className="cpMeter__marker" style={{ left: `${markerAt}%` }} />
      </div>

      {/* Stated in text, never left to be inferred from a width — and phrased
          as the DISTANCE to the line, which is the actionable number.
          "143.3% of keep below 3%" was technically true and unreadable. */}
      <div className="cpMeter__caption">
        {inverse
          ? healthy
            ? `${formatMetric(limit - value, { unit })} under the ${formatMetric(limit, { unit })} limit`
            : `${formatMetric(value - limit, { unit })} over the ${formatMetric(limit, { unit })} limit`
          : healthy
            ? `Target of ${formatMetric(limit, { unit })} met`
            : `${formatMetric(limit - value, { unit })} short of ${formatMetric(limit, { unit })}`}
        {threshold.source === "fallback" && (
          <span className="cpMeter__estimate"> · default threshold</span>
        )}
      </div>
    </div>
  );
});

export default MeterBlock;
